import { IsNotEmpty, IsOptional, IsString } from 'class-validator';
import type { OrganizationBilling, OrganizationEntitlements } from './entitlement';
import { UserRole } from './crm-user';
import { Gender } from './customer.dto';
import type { OrgPaymentGateway } from './payment/gateway';
import type {
  EmailDomainConfig,
  PortalDomainConfig,
  StripeConnectConfig
} from './self-serve.dto';
import type { ISubscriptionExtras } from './subscription.dto';

export class OrganizationSettingsNotification {
  fromEmail: string;
  replyToEmail: string;
  pharmacyEmail?: string;
  customerServiceEmail?: string;
  forgotPasswordTemplateId?: string;
  forgotPasswordSubject?: string;
  verifyAccountTemplateId?: string;
  verifyAccountSubject?: string;
  inboxMessageTemplateId?: string;
  inboxMessageSubject?: string;
  messageToSupportTemplateId?: string;
  newAdminPasswordTemplateId?: string;
  adminSignupInviteTemplateId?: string;
  activateAccountTemplateId?: string;
  customerServiceEmailSubject?: string;
}

/**
 * Apple Pay merchant-domain verification.
 *
 * Apple issues one association file per merchant identifier and expects it
 * served, byte for byte, from
 * `/.well-known/apple-developer-merchantid-domain-association` on every domain
 * registered against that merchant id. The clinic pastes the file Apple handed
 * them into Settings > Payments; patient-next serves it for its own hostname.
 *
 * Not a secret — the file is public by construction, which is what makes it
 * proof of domain control.
 */
export class ApplePayConfig {
  /** Verbatim contents of the file Apple issued. Absent or empty serves a 404. */
  domainAssociation?: string;
}

export class OrganizationSettings {
  name?: string;
  organization?: string;
  /**
   * Tenant lifecycle. `active` (or absent) appears in admin-web pickers;
   * `closed` is hidden. Open to more states later.
   */
  status?: 'active' | 'closed';
  notification?: OrganizationSettingsNotification;
  crmWebAppLayout: ICRMOrganization;
  emrWebAppLayout?: IEMRLayout;
  patientWebAppLayout?: IPatientPortalLayout;
  ecommerceEnabled: boolean;
  timezone?: string;
  customUname?: boolean;
  fromEmail?: string;
  forgotPasswordTemplateId?: string;
  verifyAccountTemplateId?: string;
  inboxMessageTemplateId?: string;
  messageToSupportTemplateId?: string;
  newAdminPasswordTemplateId?: string;
  adminSignupInviteTemplateId?: string;
  replyToEmail?: string;
  /**
   * Payment routing for this organization. Missing values use the legacy
   * payment path.
   */
  paymentGateway?: OrgPaymentGateway;
  /** Legacy-only payment-path flag; ignored by processor adapters. */
  stripeEnabled?: boolean;
  /** Apple Pay merchant-domain verification — see {@link ApplePayConfig}. */
  applePay?: ApplePayConfig;
  /**
   * Self-serve provisioning state. Written only by admin-web's self-serve
   * flows from vendor API responses — not part of the settings form's
   * writable-path allowlist.
   */
  portalDomain?: PortalDomainConfig;
  emailDomain?: EmailDomainConfig;
  stripeConnect?: StripeConnectConfig;
  blockCreateOrderOnFailedPayment?: boolean;
  defaultPauseCollection?: boolean;
  appointmentconsentSection?: string;
  apptInstruction?: string;
  patientPortalDomain?: string;
  newPatientPortalDomain?: string;
  emrEnabled?: boolean; // this is only used to assign appointments to provider in oscar emr
  // from emr
  pharmacyEmail?: string;
  customerServiceEmail?: string;
  noMarketingListId?: string;
  bigcommerceCustomerGroupId?: number;
  conversationServiceId?: string;
  adminCustomDomain?: string;
  currency?: 'cad' | 'usd';
  mainStripeOrg?: string; // The name of the organization that will have the main stripe webhooks sent to, for use with multiple organization on the same stripe. The webhook must have the org name prefixed with ~ (i.e. /~jack_health/stripe)
  pharmacies?: PharmacyConfig;
  isJjc?: boolean;
  dlqSettings?: OrganizationDlqSettings;
  stripeApplicationFeesPercentage?: number;
  cancelSubscriptionModalConfig?: {
    title?: string;
    message?: string;
    primaryCtaLabel?: string;
    primaryCtaLink?: string;
    secondaryCtaLabel?: string;
  };
  engage?: OrganizationEngageSettings;
  /**
   * Patient-chat triage agent: org-authored instructions and knowledge
   * articles. Absent means the agent runs on platform routing rules only,
   * with no clinic-specific facts.
   */
  triageAgent?: OrganizationTriageAgentSettings;
  /**
   * Plan gating for this tenant. Absent means "everything" — see
   * `resolveEntitlements` in ./entitlement.
   */
  entitlements?: OrganizationEntitlements;
  /**
   * The organization's subscription to CarePortals on the *platform* Stripe
   * account. Not the tenant's own Stripe credentials (see StripeAdapter).
   */
  billing?: OrganizationBilling;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Engage routing for this organization.
 *
 * Questionnaires listed in `qnr` are no longer answered in patient-web — the
 * patient is sent to the Engage flow whose slug equals the questionnaire key
 * (`wl` -> /o/<orgId>/wl). patient-web is decommissioned per-organization by
 * moving keys into this list, so both engines coexist during the migration.
 */
/**
 * Per-organization configuration for the patient-chat triage agent.
 *
 * Platform routing, tools, intake, and confidentiality stay in patient-api.
 * Everything clinic-specific (who the agent is, policies, reference facts)
 * lives here and is edited from admin-web Settings → Triage Agent.
 */
export type OrganizationTriageAgentSettings = {
  /**
   * Internal rollout switch. When true, patient-next routes the inbox to
   * `/chat` instead of `/messages`. Absent or false keeps the legacy inbox.
   */
  enabled?: boolean;
  /**
   * Short description interpolated into the agent's role, after the org's
   * brand name. Example: "a telehealth clinic for men's health."
   */
  companyDescription?: string;
  /**
   * Free-form instructions: support hours, refund policy, pharmacy partners,
   * tone, what not to promise. Overrides conflicting knowledge-article facts.
   */
  instructions?: string;
  /**
   * Expected pharmacy processing window. Used in order-status rules so the
   * agent does not escalate a "where's my order" question inside this window.
   * `0` or absent means no default window — follow the knowledge articles.
   */
  pharmacyProcessingBusinessDays?: number;
  /** Reference articles the agent answers factual questions from. */
  knowledgeDocs?: TriageAgentKnowledgeDoc[];
};

export type TriageAgentKnowledgeAppliesTo = 'all' | 'authenticated';

export type TriageAgentKnowledgeDoc = {
  id: string;
  topic: string;
  appliesTo: TriageAgentKnowledgeAppliesTo;
  body: string;
};

export type OrganizationEngageSettings = {
  /** Engage organization id (Mongo _id of the Engage workspace). */
  orgId?: string;
  /** Consultation/questionnaire keys handled by Engage instead of patient-web. */
  qnr?: string[];
};


export type OrganizationDlqSettings = {
  slackChannelId?: string;
  slackBotToken?: string;
  slackWebhookUrl?: string;
  emailAddresses?: string[];
};

export type PharmacyConfig = {
  [key in Pharmacy]?: IPharmacyConfig;
} & { [x: string]: IPharmacyConfig | undefined };

export type IVerificationRequirement = {
  key: string;
  name: string;
  age?: number;
};

export type IAvailableRequirement = {
  id: string;
  name: string;
  type: RequirementType;
  validityLength?: number;
  description?: string;
  extras?: {
    age?: number;
    belugaMappedFields?: Record<string, any>;
    [key: string]: any;
  };
};

export type RequirementType =
  | 'qnr'
  | 'appointment'
  | 'verification'
  | 'lab_test'
  | 'placeholder';
export class ICRMOrganization {
  isJjc?: boolean;
  title: string;
  organization?: string;
  mainMenu: AppMenuItem[];
  logo?: string;
  icon?: string;
  bookingVersion?: number; // Todo remove; not used by anyone
  emrPatientLink?: string;
  locations?: { label: string; value: string }[];
  questionnaires?: any[]; //FormTypeKeys[]; // TODO: fix this, should not import from lib/data
  verificationRequirements?: IVerificationRequirement[];
  availableRequirements?: IAvailableRequirement[];
  failedWorkflowRunsCount?: number;
}

export interface AppMenuItem {
  title: string;
  link: string;
  roles?: (UserRole | '*')[];
  icon?: string;
  key?: string;
  items?: AppMenuItem[];

  // moved from the EMR
  svg?: string;
  subMenu?: ISubMenuItem[];
  type?: 'internal' | 'external';
  name?: string;
  colSpan?: number;
  rowSpan?: number;
}

export type IPharmacyConfig = {
  _id: string;
  belugaId: string;
  states: string[];
  group?: string;
};

export interface PrescribingClinicInfo {
  name?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  fax?: string;
}

export class IEMRLayout {
  isJjc?: boolean;
  title: string;
  organization?: string;
  // TODO: this is duplicated from the OrganizationSettings
  ecommerceEnabled?: boolean;
  mainMenu: AppMenuItem[];
  customerTabs?: (
    | 'sheet'
    | 'consults'
    | 'appointments'
    | 'subscriptions'
    | 'notes'
    | 'files'
    | 'rx'
    | 'lab-requisitions'
    | 'credits'
    | 'invoices'
    | 'payment-methods'
    | 'insurance'
    | 'orders'
    | 'coupons'
    | 'extras'
    | 'activity'
    | String
  )[];

  questionnaires?: any[]; //FormTypeKeys[]; // TODO: fix this, should not import from lib/data
  verificationRequirements?: IVerificationRequirement[];
  availableRequirements?: IAvailableRequirement[];
  patientPortalDomain?: string;
  logo?: string;
  defaultPingMsg?: string;
  dosespotClinicId?: string;
  dosespotUiUrl?: string;
  belugaEnabled?: boolean;
  aiModeEnabled?: boolean;
  // will be filled from the patientWebAppLayout
  availableStates?: { [key: string]: { code: string; province: string }[] };
  prescribingClinicInfo?: PrescribingClinicInfo; // will be used in manual prescription creation
}

export interface AppMenuItem {
  title: string;
  link: string;
  roles?: (UserRole | '*')[];
  icon?: string;
  key?: string;
  items?: AppMenuItem[];
  target?: '_self' | '_blank';

  // moved from the EMR
  svg?: string;
  subMenu?: ISubMenuItem[];
  type?: 'internal' | 'external';
  name?: string;

  patientPortalType?: 'nav' | 'menu';
}

export interface ISubMenuItem extends AppMenuItem {
  subMenu?: undefined;
}

export enum Pharmacy {
  ValiantRx = 'ValiantRx',
  Belmar = 'Belmar',
  Precision = 'Precision',
  Curaxa = 'Curaxa',
  SouthEnd = 'SouthEnd',
  Strive = 'Strive',
  RxCompoundStore = 'RxCompoundStore',
  TownAndCountry = 'TownAndCountry',
  WellsRx = 'WellsRx',
  GogoMed = 'GogoMed',
  Empower = 'Empower',
  ThePharmacyHub = 'ThePharmacyHub',
  Curexa = 'Curexa',
  WPlabs = 'WPlabs',
  Foothills = 'Foothills',
  Promise = 'Promise',
  Medcal = 'Medcal',
  UKPharmacy = 'UKPharmacy',
  Partell = 'Partell',
  pharmacyHub = 'pharmacyHub',
  Beaker = 'Beaker'
}

export type MantineSupportedColors =
  | 'dark'
  | 'gray'
  | 'red'
  | 'pink'
  | 'grape'
  | 'violet'
  | 'indigo'
  | 'blue'
  | 'cyan'
  | 'green'
  | 'lime'
  | 'yellow'
  | 'orange'
  | 'teal';

export type Color = MantineSupportedColors | (string & {});

export interface IConsentCheckbox {
  /** Label shown next to the checkbox. May contain HTML (e.g. links). */
  labelHtml: string;
  /** When true the box is not mandatory. Omitted/false => the patient must check it to sign up. */
  optional?: boolean;
}

/** Google Tag Manager config on the patient portal layout. */
export class IPatientPortalGtm {
  /** Container ID (e.g. `GTM-XXXXXXX`). Empty disables GTM injection. */
  id: string;
  /** Optional custom / server-side GTM script URL. Falls back to the standard endpoint when empty. */
  url?: string;
}

export class IPatientPortalLayout {
  title: string;
  brand: string;
  organization?: string;
  theme?: string;
  isJjc?: boolean;
  colors?: Partial<{
    headerColor: Color;
    headerTextColor: Color;
    backgroundColor: Color;
    backgroundAltColor: Color;
    primaryColor: Color;
    primaryTextColor: Color;
    secondaryColor: Color;
    secondaryTextColor: Color;
    tertiaryColor: Color;
    tertiaryTextColor: Color;
    lightColor: Color;
    textColor: Color;
    mainFontFamily: string;

    // Font sizes
    fontSizeH1: string;
    fontSizeH1Mobile: string;
    fontSizeH2: string;
    fontSizeH2Mobile: string;
    fontSizeH3: string;
    fontSizeH3Mobile: string;
    fontSizeH4: string;
    fontSizeH4Mobile: string;
    fontSizeH5: string;
    fontSizeH5Mobile: string;
    fontSizeH6: string;
    fontSizeH6Mobile: string;
    fontSizeH3Subhead: string;
    fontSizeH3SubheadMobile: string;
    fontSizeH4Subhead: string;
    fontSizeH4SubheadMobile: string;

    // Body font sizes
    fontSizeBody1: string;
    fontSizeBody1Mobile: string;
    fontSizeBody2: string;
    fontSizeBody2Mobile: string;
    fontSizeBody3: string;
    fontSizeBody3Mobile: string;
    fontSizeBody4: string;
    fontSizeBody4Mobile: string;

    // Button font sizes
    fontSizeButton: string;
    fontSizeButtonMobile: string;
    fontSizeFormButton: string;
    fontSizeFormButtonMobile: string;

    // react patient portals colors (css variables), will override legacy colors above
    '--background': string;
    '--foreground': string;
    '--card': string;
    '--card-foreground': string;
    '--popover': string;
    '--popover-foreground': string;
    '--primary': string;
    '--primary-foreground': string;
    '--secondary': string;
    '--secondary-foreground': string;
    '--muted': string;
    '--muted-foreground': string;
    '--accent': string;
    '--accent-foreground': string;
    '--destructive': string;
    '--destructive-foreground': string;
    '--border': string;
    '--input': string;
    '--ring': string;
    '--radius': string;
    '--chart-1': string;
    '--chart-2': string;
    '--chart-3': string;
    '--chart-4': string;
    '--chart-5': string;
    '--header-background': string;
    '--header-foreground': string;
    '--background-alt': string;
    '--nav-background': string;
    '--gradient-background-from': string;
    '--gradient-background-to': string;
  }>;
  mainMenu: AppMenuItem[];
  /**
   * Projected from `triageAgent.enabled`. When true, patient-next uses
   * `/chat` for inbox nav (and rewrites `mainMenu` links that pointed at
   * `/messages`). Only the boolean is public — knowledge docs stay off this
   * payload.
   */
  triageAgentEnabled?: boolean;
  /**
   * Projected from `applePay.domainAssociation`, not stored on the layout.
   * patient-next serves it verbatim at
   * `/.well-known/apple-developer-merchantid-domain-association`; it rides on
   * this payload because the portal has no database of its own, and the file
   * is public by construction — it is what proves the domain is the clinic's.
   */
  applePayDomainAssociation?: string;
  cartSummarySection?: string;
  availableStates?: { [key: string]: { code: string; province: string }[] };
  topAction?: {
    title: string;
    link: string;
  };
  logo?: {
    img?: string;
    link?: string;
  };
  favicon?: string;
  referrals?: {
    enabled: boolean;
    referrerAmount: number;
    refereeAmount: number;
    referralURI?: string;
    referralFAQ?: string;
  };
  allowRxForward?: boolean; // enables a button the patient portal to forward the RX to their selected pharmacy
  nmiPublicKey?: string;
  stripePublicKey?: string;
  /**
   * Whop company id (`biz_…`). Not a secret — Whop checkout URLs expose it —
   * and it is the "is Whop configured" signal the checkout page needs, playing
   * the role the public keys play for Stripe/NMI. Whop *secrets* live in
   * ParamStore under `/{org}/WHOP/*` like every other adapter credential.
   */
  whopCompanyId?: string;
  /** Legacy routing input used by the shared payment processor resolver. */
  stripeEnabled?: boolean;
  /**
   * The org's raw payment gateway setting. Explicit processors use the modern
   * checkout; `legacy`/absent is resolved together with `stripeEnabled`.
   */
  paymentGateway?: OrgPaymentGateway;
  shopUrl: string;
  /**
   * Google Tag Manager container for the patient portal / storefront.
   * When `id` is empty, the portal does not inject the GTM script.
   * When `url` is empty, the standard googletagmanager.com endpoint is used.
   */
  gtm?: IPatientPortalGtm;
  includedCheckoutServices?: {
    name: string;
    price: number;
  }[];
  checkoutWhatsIncluded?: {
    header: string;
    items: {
      icon?: string;
      name: string;
    }[];
    footer: string;
  };
  privacyPolicyUrl: string;
  termsOfUseUrl: string;
  supportUrl?: string;
  defaultGender?: Gender;
  country?: 'CA' | 'US';
  personaTemplateId?: string;
  productPlaceholderImage?: string;
  enableResubmitOnIdVerification?: boolean;
  isSelfieRequiredForVerification?: boolean;
  enablesPharmacySelectionAtCheckout?: boolean;
  hideResubmitOnIdVerification?: boolean;
  isEmailVerificationEnabled?: boolean;
  newPatientPortalDomain?: string;
  enableManualVerification?: boolean;
  /**
   * JJC: Mongo _id of the catalog product used as the pharmacy transfer-out fee line.
   * Amount and label come from that product’s `price` / `listPrice` and `label` at checkout time.
   */
  transferOutProductId?: string;
  /**
   * JJC: product **category** Mongo _ids. The flat fee is waived only when **every** medication line’s
   * product has **at least one** category in this list. If **any** line has no category in the list,
   * the flat fee is charged.
   */
  transferOutFeeExemptCategoryIds?: string[];
  termsOfServiceUrl?: string | null = null;
  isSMSConsentRequired?: boolean = false;
  consents?: Record<string, IConsentCheckbox>;
  subscription?: ISubscriptionExtras;
  cancelSubscriptionModalConfig?: {
    title?: string;
    message?: string;
    primaryCtaLabel?: string;
    primaryCtaLink?: string;
    secondaryCtaLabel?: string;
  };
}

class UpdateSettingsNotification {
  @IsNotEmpty()
  @IsString()
  fromEmail: string;

  @IsNotEmpty()
  @IsString()
  replyToEmail: string;

  @IsString()
  @IsOptional()
  pharmacyEmail?: string;

  @IsString()
  @IsOptional()
  customerServiceEmail?: string;

  @IsString()
  @IsOptional()
  forgotPasswordSubject?: string;

  @IsString()
  @IsOptional()
  forgotPasswordTemplateId?: string;

  @IsString()
  @IsOptional()
  verifyAccountSubject?: string;

  @IsString()
  @IsOptional()
  verifyAccountTemplateId?: string;

  @IsString()
  @IsOptional()
  inboxMessageTemplateId?: string;

  @IsString()
  @IsOptional()
  inboxMessageSubject?: string;

  @IsString()
  @IsOptional()
  newAdminPasswordTemplateId?: string;
}

/**
 * Recursively-optional variant of a type. Arrays are treated as leaves (replaced
 * wholesale, not merged element-by-element) so callers may send only the nested
 * fields they actually changed.
 */
export type DeepPartial<T> = T extends (infer U)[]
  ? U[]
  : T extends object
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : T;

/**
 * Partial organization document for PUT /crm/settings (internal users only).
 * Deep-partial: each settings tab sends only the (possibly nested) fields it
 * owns, and the API updates just those leaf paths — see
 * `OrganizationService.update`.
 */
export type UpdateSettingsDto = DeepPartial<OrganizationSettings>;
